import {
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react";

export function FileUpload({
  label,
  helperText,
  accept,
  multiple = false,
  disabled = false,
  clearable = false,
  onFilesChange,
  className,
}: {
  label: string;
  helperText?: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  onFilesChange: (files: File[]) => void;
  className?: string;
}) {
  const id = useId();
  const helperId = `${id}-helper`;
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [selection, setSelection] = useState("");

  const selectFiles = (files: File[]) => {
    setSelection(files.map((file) => file.name).join(", "));
    onFilesChange(files);
  };

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    selectFiles(Array.from(event.target.files ?? []));
  };

  const onDragOver = (event: DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = "copy";
    setDragging(true);
  };

  const onDragLeave = (event: DragEvent<HTMLLabelElement>) => {
    if (
      event.relatedTarget instanceof Node &&
      event.currentTarget.contains(event.relatedTarget)
    ) {
      return;
    }
    setDragging(false);
  };

  const onDrop = (event: DragEvent<HTMLLabelElement>) => {
    if (disabled) return;
    event.preventDefault();
    setDragging(false);
    selectFiles(Array.from(event.dataTransfer.files));
  };

  const clearSelection = () => {
    if (inputRef.current) inputRef.current.value = "";
    setSelection("");
    onFilesChange([]);
  };

  return (
    <div className={["ui-file-upload", className].filter(Boolean).join(" ")}>
      <div className="ui-file-upload__heading">
        <span className="ui-file-upload__label">{label}</span>
        {clearable && selection && (
          <button
            type="button"
            className="ui-file-upload__clear"
            disabled={disabled}
            aria-label={`Clear ${label}`}
            onClick={clearSelection}
          >
            Remove
          </button>
        )}
      </div>
      <label
        className={[
          "ui-file-upload__drop-zone",
          dragging ? "ui-file-upload__drop-zone--dragging" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        htmlFor={id}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          id={id}
          className="ui-file-upload__input"
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          aria-label={label}
          aria-describedby={helperText ? helperId : undefined}
          onChange={onChange}
        />
        <span className="ui-file-upload__icon" aria-hidden="true">
          ↑
        </span>
        <span className="ui-file-upload__title">
          {selection || "Drop files here or choose files"}
        </span>
        {selection ? (
          <span className="ui-file-upload__detail">
            Drop or choose {multiple ? "other files" : "another file"} to
            replace this selection
          </span>
        ) : (
          <span className="ui-file-upload__detail">
            {accept ? accept.replaceAll(",", ", ") : "Any file type"}
          </span>
        )}
      </label>
      {helperText && <small id={helperId}>{helperText}</small>}
    </div>
  );
}
